/**
 * UsInstitutionSignals — Section 5 (Teaching) + Section 8 (Outcomes)
 * signals for US bachelor programmes:
 *
 *   • Section 5 · Teaching quality proxies from Scorecard:
 *     - 4-year graduation rate
 *     - International student %
 *     - Gender balance
 *   • Section 8 · US national baseline per subject area (NACE + BLS)
 *
 * Renders nothing when the row isn't Scorecard-sourced or the uni
 * lacks the outcomes/aid subdocs.
 */
import { Card } from "@/components/ui/card";
import { TrendingUp, GraduationCap, ExternalLink } from "lucide-react";
import { getUsBaseline } from "@/data/usSubjectBaselines";

export function UsInstitutionSignals({ degree, uni }) {
  if (degree?.source_provenance?.source !== "college_scorecard_api") return null;
  const so = uni?.scorecard_outcomes;
  if (!so) return null;

  const pct = (n) => (n == null ? "—" : `${Math.round(n * 100)}%`);
  const items = [
    ["4-year graduation rate",   pct(so.graduation_rate_4yr)],
    ["1st-year retention",       pct(so.retention_rate_1yr)],
    ["International students",   pct(so.intl_student_rate)],
    ["Women in cohort",          pct(so.share_women)],
    ["Non-traditional (25+)",    pct(so.share_over_25)],
  ].filter(([, v]) => v && v !== "—");
  if (!items.length) return null;

  return (
    <Card
      className="mt-4 p-4 border-sky-300 bg-sky-50/40"
      data-testid="us-institution-signals"
    >
      <div className="flex items-center gap-2 mb-3">
        <GraduationCap className="h-4 w-4 text-sky-800" />
        <div className="font-underground-sc text-[10px] tracking-wider text-sky-900">
          Institutional signals · {uni.name}
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-x-6 gap-y-1.5 text-sm text-stone-800">
        {items.map(([label, val]) => (
          <div key={label}>
            <b>{label}:</b> {val}
          </div>
        ))}
      </div>
      <div className="mt-3 text-[11px] font-mono text-stone-500">
        Source: US Dept of Education Scorecard · verified {so.fetched_at}
      </div>
    </Card>
  );
}


export function UsSubjectBaseline({ degree }) {
  if (degree?.source_provenance?.source !== "college_scorecard_api") return null;
  const subject = degree?.subject_area;
  const b = getUsBaseline(subject);
  if (!b) return null;
  const usd = (n) => `$${n.toLocaleString()}`;
  const pct = (n) => `${Math.round(n * 100)}%`;

  return (
    <Card
      className="mt-4 p-4 border-stone-300 bg-white"
      data-testid="us-subject-baseline"
    >
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="h-4 w-4 text-stone-600" />
        <div className="font-underground-sc text-[10px] tracking-wider text-stone-600">
          US national baseline · {subject || "All fields"}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-x-8 gap-y-1.5 text-sm text-stone-800">
        <div><b>Median starting salary:</b> {usd(b.median_starting_salary_usd)}/yr</div>
        <div><b>Median wage (all experience):</b> {usd(b.median_wage_2024_usd)}/yr</div>
        <div><b>Employment rate (6 mo):</b> {pct(b.employment_rate)}</div>
        <div><b>10-yr projected job growth:</b> {b.growth_10y >= 0 ? "+" : ""}{Math.round(b.growth_10y * 100)}%</div>
      </div>
      {b.notes && (
        <div className="mt-3 text-xs text-stone-600 italic">{b.notes}</div>
      )}
      <div className="mt-3 text-[11px] font-mono text-stone-500 flex items-center gap-1">
        <span>National average · not degree-specific ·</span>
        <a
          href={b.source_url}
          target="_blank"
          rel="noreferrer"
          className="text-[#4C1D95] hover:underline inline-flex items-center gap-1"
        >
          {b.source_name} <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </Card>
  );
}
